import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useDepartment } from '../context/DepartmentContext';

const DepartmentSelector = ({ targetRoute, title, subtitle }) => {
  const { departments, selectedDepartment, selectDepartment, loading, error } = useDepartment();
  const [open, setOpen] = useState(true);
  const [localDepartment, setLocalDepartment] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // If we already have a selected department, use it
    if (selectedDepartment) {
      setLocalDepartment(selectedDepartment.department_id);
    }
  }, [selectedDepartment]);

  const handleClose = () => {
    setOpen(false);
    navigate(-1); // Go back to previous page
  };

  const handleContinue = () => {
    if (localDepartment) {
      const department = departments.find(dept => dept.department_id === parseInt(localDepartment));
      selectDepartment(department);
      setOpen(false);
      navigate(targetRoute);
    }
  };

  const handleChange = (event) => {
    setLocalDepartment(event.target.value);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6">{title || 'Select Department'}</Typography>
      </DialogTitle>
      <DialogContent>
        <AnimatePresence mode="wait">
          {loading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                >
                  <CircularProgress />
                </motion.div>
              </Box>
            </motion.div>
          ) : error ? (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
            >
              <Typography color="error">{error}</Typography>
            </motion.div>
          ) : (
            <motion.div
              key="content"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
            >
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  {subtitle || 'Please select a department to continue'}
                </Typography>
              </motion.div>
              
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <FormControl fullWidth sx={{ mt: 1 }}>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={localDepartment}
                    label="Department"
                    onChange={handleChange}
                  >
                    {departments.map((department, index) => (
                      <MenuItem 
                        component={motion.div}
                        whileHover={{ 
                          scale: 1.02,
                          backgroundColor: 'rgba(25, 118, 210, 0.08)'
                        }}
                        key={department.department_id} 
                        value={department.department_id}
                      >
                        {department.department_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
      <DialogActions>
        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <Button onClick={handleClose}>Cancel</Button>
        </motion.div>
        
        <motion.div
          whileHover={{ scale: localDepartment && !loading ? 1.05 : 1 }}
          whileTap={{ scale: localDepartment && !loading ? 0.95 : 1 }}
        >
          <Button 
            onClick={handleContinue} 
            variant="contained" 
            color="primary"
            disabled={!localDepartment || loading}
            sx={{
              boxShadow: 'none',
              '&:hover': {
                boxShadow: localDepartment && !loading ? '0 4px 10px rgba(25, 118, 210, 0.25)' : 'none'
              }
            }}
          >
            Continue
          </Button>
        </motion.div>
      </DialogActions>
    </Dialog>
  );
};

export default DepartmentSelector;
